import {reactive, toRefs, computed} from "vue";
import type { ComputedRef, ToRefs } from "vue";
import Place from "@wikicasa/common/model/Place";
import RealEstateSearchBean, {getDefaultSearchBean} from "@wikicasa/common/model/RealEstateSearchBean";
//@ts-ignore
import {ENERGY_CLASS} from "@customTypes/EnergyClass";
//@ts-ignore
import {isArrNullOrEmpty} from "@utils/ArrayUtils";
//@ts-ignore
import {deepCopy} from "@utils/ObjectUtils";
import IPlace from "@wikicasa/common/model/Place";
//@ts-ignore
import {Nullable} from "@customTypes/CustomUtilityTypes";
//@ts-ignore
import {PLACE_TYPE} from "@customTypes/PlaceType";
import IRealEstateSearchBean from "@wikicasa/common/model/RealEstateSearchBean";

type OptionalAction = (() => void) | undefined;


interface UsablePlaceTypeState {
    localityList: IPlace[];
    zoneList: IPlace[];
    searchBeanModal: IRealEstateSearchBean;
    prevSelectedLocality: IPlace | undefined;
    localitySelected: IPlace | undefined;
    prevZoneIDsSelected: number[] | undefined;
    zoneIDsSelected: number[];
    zoneModifiedByUser: boolean;
    districtSelected: Record<number, number[]>;
    districtsObj: Record<number, IPlace[]>;
    lastInsertedZone: Nullable<number>;
}

interface UsablePlaceType extends ToRefs<UsablePlaceTypeState>{
    isSingleDistrictSelected: ComputedRef<boolean>;
    isAnyDistrictSelected: ComputedRef<boolean>;
    selectedDistrictLength: ComputedRef<number>;
    partialSelectedDistrictLength: ComputedRef<number>;
    allDistrictSelectedPerZone: ComputedRef<boolean>;
    convertEnergyClassFilter: () => void;
    convertFloor: () => void;
    resetSearchBeanZonesAndLocalities: () => void;
    updateSearchModal: (searchBean: IRealEstateSearchBean) => void;
    updateSearchModalByPlace: (plc: IPlace) => void;
    updateSearchModalAndPlace: (plc: IPlace, updatePlace: () => void) => void;
    fixSearchBean: (updateZoneCb: () => (string | undefined), updateLocationCb: () => (string | undefined), updateDistrictCb: () => (string | undefined)) => void;
    initZonesAndLocalities: (placesPromise: Promise<IPlace[]>, onSuccess?: OptionalAction, onFailure?: OptionalAction) => Promise<void>;
    areAllDistrictsSelected: (zoneId: number) => boolean;
    isSingleDistrictByZoneId: (zoneId: number) => boolean;
    isZoneOrLocalitySelected: () => boolean;
    isZoneWithoutDistrict: (zoneId: number) => boolean;
    areZonesAndLocalitiesEmpty: () => boolean;
    areZonesAndLocalitiesFilled: () => boolean;
    getSingleSelectedZone: () => (IPlace | null);
    getSingleSelectedDistrict: () => Nullable<IPlace>;
    getZoneById: (zoneID: (number | null | undefined)) => (IPlace | null);
    getDistrictById: (zoneId: Nullable<number>, districtID: (number | null | undefined)) => (IPlace | null);
    getDistrictLengthByZoneId: (zoneId: number) => number;
    getDistrictSelectedLengthByZoneId: (zoneId: number) => Nullable<number>;
    resetZoneAndLocalities: () => void;
    addZone: (zone: IPlace, updatePlaceNameAction: () => void) => void;
    removeZone: (zone: IPlace, updatePlaceNameAction: () => void, isDistrict?: boolean) => void;
    isZoneSelected: (zoneId: number) => boolean;
    isLocationSelected: (locationId: number) => boolean;
    isDistrictSelected: (zoneId: number, districtId: number) => boolean;
    addLocation: (place: IPlace, updatePlaceNameAction: () => void) => void;
    removeLocation: (updatePlaceNameAction: () => void) => void;
    insertDistrictByZoneId: (zoneId: number, districtId: number) => void;
    insertBulkDistrictByZoneId: (zoneId: number, districtId: number[]) => void;
    onPlaceLIClick: (place: IPlace, updatePlaceNameAction: () => void, isLocation?: boolean) => void;
    onDistrictClick: (zone: IPlace, district: IPlace, updatePlaceNameAction: () => void) => void;
    addDistrict: (zone: IPlace, updatePlaceNameAction: () => void, district?: IPlace) => void;
    removeDistrict: (zone: IPlace, updatePlaceNameAction: () => void, district?: IPlace) => void;
}

export function usePlaceType(): UsablePlaceType {
    const state = reactive<UsablePlaceTypeState>({
        localityList: [] ,
        /**
         * The zone list resulting from the city search
         * */
        zoneList: [],
        searchBeanModal: getDefaultSearchBean(),
        prevSelectedLocality: undefined, //TODO: valutare se inserirlo solo in placeModal
        localitySelected: undefined,
        prevZoneIDsSelected: undefined, //TODO: valutare se inserirlo solo in placeModal
        /**
         * The zone ids checked (from the dropdown)
         * */
        zoneIDsSelected: [],
        zoneModifiedByUser: false,
        districtSelected: {},
        districtsObj: {},
        lastInsertedZone: 0
    })

    const isSingleDistrictSelected = computed((): boolean => {
        return selectedDistrictLength.value === 1
    })
    const isAnyDistrictSelected = computed((): boolean => {
        return Object.keys(state.districtSelected).length > 0;
    })
    const selectedDistrictLength = computed((): number => {
        return Object.values(state.districtSelected).reduce((prev, cur) => {
            return prev + cur.length
        }, 0)
    })
    const partialSelectedDistrictLength = computed((): number => {

        return Object.entries(state.districtSelected).reduce((prev, [zoneId, districts]) => {
            const totalDistrictLength = getDistrictLengthByZoneId(+zoneId)

            if (totalDistrictLength === districts.length)
                return prev

            return prev + districts.length
        }, 0)
    })
    const allDistrictSelectedPerZone = computed((): boolean => {
        if (!isAnyDistrictSelected.value) {
            return false
        }
        for (const zoneId in state.districtSelected) {
            const districtSelectedLength = state.districtSelected[zoneId].length
            const districtLength = state.districtsObj[zoneId].length
            if (districtSelectedLength !== districtLength)
                return false
        }

        return true
    })

    const convertEnergyClassFilter = (): void  => {
        switch (state.searchBeanModal.energyClassFilter) {
            case "HIGH": {
                state.searchBeanModal.energyClassFilter = ENERGY_CLASS.HIGH;
                break;
            }
            case "MEDIUM": {
                state.searchBeanModal.energyClassFilter = ENERGY_CLASS.MEDIUM;
                break;
            }
            case "LOW": {
                state.searchBeanModal.energyClassFilter = ENERGY_CLASS.LOW;
                break;
            }
        }
    }
    const convertFloor = (): void => {
        state.searchBeanModal.floor = state.searchBeanModal.floorId;
    }
    /* SearchBeanModal handling */
    const resetSearchBeanZonesAndLocalities = (): void => {
        state.zoneList = []
        state.zoneIDsSelected = [];
        state.districtSelected = {};

        state.localitySelected = undefined;

        state.searchBeanModal.zoneIdList = [];
        state.searchBeanModal.localityIdList = [];
        state.searchBeanModal.districtIdList = [];

        state.searchBeanModal.zoneId = null;
        state.searchBeanModal.zoneURL = null;
        state.searchBeanModal.districtId = null;
        state.searchBeanModal.districtURL = null;
    }
    const updateSearchModal = (searchBean: RealEstateSearchBean): void => {
        const _searchBean = deepCopy(searchBean);
        if (!_searchBean)
            throw new Error("_searchBean is null");
        state.searchBeanModal = _searchBean;
    }
    const updateSearchModalByPlace = (plc: Place): void => {
        state.searchBeanModal.areaOfInterestId = plc.areaOfInterestID;
        state.searchBeanModal.cityURL = plc.cityURL;
        state.searchBeanModal.cityDisplayName = plc.displayName;
        state.searchBeanModal.cityId = plc.cityID;
        state.searchBeanModal.placeType = plc.placeType;
        state.searchBeanModal.placeId = plc.placeID;
        state.searchBeanModal.provinceId = plc.provinceID;
        state.searchBeanModal.regionId = plc.regionID;
        state.searchBeanModal.zoneId = plc.zoneID;
        state.searchBeanModal.zoneIdList = [];
        state.searchBeanModal.localityIdList = [];
        state.searchBeanModal.polygonFromMap = "";
        state.searchBeanModal.polygonForDB = "";
        state.searchBeanModal.localityIdList = [];
        state.searchBeanModal.zoneURL = plc.zoneURL;
        state.searchBeanModal.districtId = plc.districtID;
        //The zoneIDList is used to update the counter!
        if (plc.zoneID)
            state.searchBeanModal.zoneIdList = [plc.zoneID];
        //Same for the localityIdList!
        if (plc.localityID)
            state.searchBeanModal.localityIdList = [plc.localityID];

        if (plc.placeType === PLACE_TYPE.DISTRICT) {
            state.searchBeanModal.districtIdList = [plc.districtID]
            state.searchBeanModal.districtId = plc.districtID
            state.searchBeanModal.zoneId = null
            state.searchBeanModal.zoneIdList = []
        }

    }
    const updateSearchModalAndPlace = (plc: Place, updatePlace: () => void): void => {
        updateSearchModalByPlace(plc);
        updatePlace();
    }
    const fixSearchBean = (updateZoneCb: () => string | undefined, updateLocationCb: () => string | undefined, updateDistrictCb: () => string | undefined): void => {
        const {localityIdList, zoneIdList, districtIdList} = state.searchBeanModal;
        if (localityIdList && localityIdList.length) {
            state.searchBeanModal.localityIdList = [...localityIdList];
            state.searchBeanModal.zoneURL = updateLocationCb();
            return;
        }

        if (zoneIdList) {
            if (zoneIdList.length == 1) {
                state.searchBeanModal.zoneId = zoneIdList[0];
                state.searchBeanModal.zoneIdList = [];
                state.searchBeanModal.zoneURL = updateZoneCb();
            } else if (zoneIdList.length > 1) {
                // remove duplicate IDS
                state.searchBeanModal.zoneIdList = [...new Set(state.searchBeanModal.zoneIdList)];
                state.searchBeanModal.zoneURL = null;
                state.searchBeanModal.zoneId = null;
            }
        } else if (districtIdList) {
            if (districtIdList.length == 1) {
                state.searchBeanModal.districtId = districtIdList[0];
                state.searchBeanModal.districtIdList = [];
                state.searchBeanModal.districtURL = updateDistrictCb();
            } else if (districtIdList.length > 1) {
                // remove duplicate IDS
                //state.searchBeanModal.districtIdList = [...new Set(state.searchBeanModal.districtIdList)];
                state.searchBeanModal.districtURL = null;
                state.searchBeanModal.districtId = null;
            }
        } else {
            state.searchBeanModal.zoneURL = null;
            state.searchBeanModal.zoneId = null;
            state.searchBeanModal.districtURL = null;
            state.searchBeanModal.districtId = null;
        }
    }
    /* Zone and Location handling */
    const  initZonesAndLocalities = async (
        placesPromise: Promise<Place[]>,
        onSuccess: OptionalAction = undefined,
        onFailure: OptionalAction = undefined
    ): Promise<void> => {
        const places = await placesPromise;

        if (!places || places.length <= 0) {
            onFailure && onFailure()
            // throw new Error("empty or null places");
        }

        const localities: Place[] = [];
        const zones: Place[] = [];

        places.forEach(p => {
            if (p.zoneID)
                zones.push(p);
            else if (p.localityID)
                localities.push(p);
        })

        state.localityList = localities;
        state.zoneList = zones;

        let selectedDistrictIds = 0
        state.districtsObj = {}
        state.districtSelected = {}

        for (const zone of zones) {
            //If there are no districts, we continue our loop
            if (zone.subPlaces.length <= 0)
                continue

            // $set(state.districtsObj, zone.zoneID, zone.subPlaces)
            state.districtsObj[zone.zoneID] = zone.subPlaces


            if (selectedDistrictIds < (state.searchBeanModal.districtIdList?.length || -1))
                //Initializing districts selected
                state.districtsObj[zone.zoneID]
                    .forEach(district => {
                        if (state.searchBeanModal.districtIdList?.includes(district.districtID)) {
                            insertDistrictByZoneId(zone.zoneID, district.districtID)
                            ++selectedDistrictIds
                        }
                    })
        }

        onSuccess && onSuccess();
    }
    const areAllDistrictsSelected = (zoneId: number): boolean => {
        return state.zoneIDsSelected.includes(zoneId)
    }
    const isSingleDistrictByZoneId = (zoneId: number): boolean => {
        if (!state.districtSelected[zoneId])
            return false

        return state.districtSelected[zoneId].length === 1
    }
    const isZoneOrLocalitySelected = (): boolean => {
        return state.zoneIDsSelected.length > 0 || state.localitySelected != null;
    }
    const isZoneWithoutDistrict = (zoneId: number): boolean => {
        return !Object.keys(state.districtsObj).includes(`${zoneId}`)
    }
    const  areZonesAndLocalitiesEmpty = (): boolean => {
        return isArrNullOrEmpty(state.zoneList) && isArrNullOrEmpty(state.localityList);
    }
    const areZonesAndLocalitiesFilled = (): boolean => {
        return !isArrNullOrEmpty(state.zoneList) && !isArrNullOrEmpty(state.localityList);
    }
    const getSingleSelectedZone = (): Place | null => {
        if (state.zoneIDsSelected.length !== 1 && !state.searchBeanModal.zoneId)
            return null;

        const currentZoneID = state.zoneIDsSelected.length == 1 ? state.zoneIDsSelected[0] : state.searchBeanModal.zoneId;
        return getZoneById(currentZoneID);
    }
    const getSingleSelectedDistrict = (): Nullable<Place> => {

        if (Object.keys(state.districtSelected).length !== 1 &&
            (state.searchBeanModal.districtIdList?.length !== 1 || !state.searchBeanModal.districtId))
            return null;

        const districtSelectedKeys = Object.keys(state.districtSelected)
        const singleZoneId = districtSelectedKeys[0]

        return getDistrictById(+singleZoneId, Object.values(state.districtSelected)[0][0])
    }
    const getZoneById = (zoneID: number | null | undefined): Place | null => {
        if (!zoneID)
            return null;


        for (const plc of state.zoneList)
            if (plc.zoneID === zoneID) {
                return plc;
            }

        return null;
    }
    const getDistrictById = (zoneId: Nullable<number>, districtID: number | null | undefined): Place | null => {
        if (!districtID || !zoneId) {
            return null;
        }

        for (const plc of state.districtsObj[zoneId])
            if (plc.districtID === districtID) {
                return plc;
            }

        return null;
    }
    const getDistrictLengthByZoneId = (zoneId: number): number => {
        return state.districtsObj[zoneId].length;
    }
    const getDistrictSelectedLengthByZoneId = (zoneId: number): Nullable<number> => {
        if (!state.districtSelected[zoneId])
            return null

        return state.districtSelected[zoneId].length
    }
    const resetZoneAndLocalities = (): void => {
        if (isArrNullOrEmpty(state.zoneList) && isArrNullOrEmpty(state.localityList)) {
            return;
        }


        state.zoneList = [];
        state.localityList = [];
    }

    const addZone = (zone: Place, updatePlaceNameAction: () => void): void => {
        state.zoneIDsSelected = [...state.zoneIDsSelected, zone.zoneID]

        state.lastInsertedZone = zone.zoneID

        addDistrict(zone, updatePlaceNameAction);
        if (state.searchBeanModal.zoneIdList)
            state.searchBeanModal.zoneIdList = [...state.searchBeanModal.zoneIdList, zone.zoneID]

        state.zoneModifiedByUser = true;

        if (state.zoneIDsSelected.length === 1) {
            state.searchBeanModal.zoneURL = zone.zoneURL
            state.searchBeanModal.zoneId = zone.zoneID
        } else {
            state.searchBeanModal.zoneURL = null;
            state.searchBeanModal.zoneId = null;
        }
        updatePlaceNameAction();
    }
    const removeZone = (zone: Place, updatePlaceNameAction: () => void, isDistrict = false): void => {
        state.zoneIDsSelected = state.zoneIDsSelected.filter((id: number) => id !== zone.zoneID) || [];
        if (!isDistrict)
            removeDistrict(zone, updatePlaceNameAction);
        state.searchBeanModal.zoneIdList = state.searchBeanModal?.zoneIdList?.filter((id: number) => id !== zone.zoneID) || [];
        state.searchBeanModal.zoneId = null
        state.searchBeanModal.zoneURL = null
        state.zoneModifiedByUser = true;

        if (state.zoneIDsSelected.length === 1) {
            const singleZone = state.zoneList.find(z => z.zoneID === state.zoneIDsSelected[0]);
            if (singleZone) {
                state.searchBeanModal.zoneURL = singleZone.zoneURL;
                state.searchBeanModal.zoneId = singleZone.zoneID;
            }
        }

        updatePlaceNameAction();
    }
    const isZoneSelected = (zoneId: number): boolean => {
        return state.zoneIDsSelected.includes(zoneId)
            || !!state.districtsObj[zoneId]?.find(district => state.districtSelected[zoneId]?.includes(district.districtID));
    }
    const isLocationSelected = (locationId: number): boolean => {
        return state.localitySelected?.localityID === locationId;
    }
    const isDistrictSelected = (zoneId: number, districtId: number): boolean => {
        if (!state.districtSelected[zoneId])
            return false

        return state.districtSelected[zoneId].includes(districtId);
    }

    /**
     * Add a single location to the zoneIdSelected list.
     * This method ensures that the zoneIdSelected list will contain ONLY the locationId passed as input parameter
     * */
    const addLocation = (place: Place, updatePlaceNameAction: () => void): void => {
        state.zoneIDsSelected = [];
        state.searchBeanModal.zoneIdList = [];
        /*
                if(state.localitySelected !== undefined)
                    $set(state.localitySelected, "localitySelected", place as Place)*/
        state.localitySelected = place;

        if (state.searchBeanModal) {
            state.searchBeanModal.localityIdList = [place.localityID];
            state.searchBeanModal.zoneURL = place.zoneURL;
        }

        state.zoneModifiedByUser = true;
        updatePlaceNameAction();
    }
    const removeLocation = (updatePlaceNameAction: () => void): void => {
        state.localitySelected = undefined;

        state.searchBeanModal.localityIdList = [];
        state.searchBeanModal.zoneURL = "";
        state.zoneModifiedByUser = true;

        updatePlaceNameAction();
    }
    const insertDistrictByZoneId = (zoneId: number, districtId: number): void => {
        if (!state.districtSelected[zoneId]) {
            // $set(state.districtSelected, `${zoneId}`, [districtId])
            state.districtSelected[zoneId] = [districtId]
        } else {
            state.districtSelected[zoneId] = [...state.districtSelected[zoneId], districtId]
        }
    }
    const insertBulkDistrictByZoneId = (zoneId: number, districtId: number[]): void => {
        // $set(state.districtSelected, `${zoneId}`, districtId)
        state.districtSelected[zoneId] = districtId
    }
    const onPlaceLIClick = (place: Place, updatePlaceNameAction: () => void, isLocation = false): void => {
        //Sebastian R.S: If we select a zone, we want to remove the ONLY selected locality if present
        if (place.zoneID && state.localitySelected)
            removeLocation(updatePlaceNameAction)
        if (isLocation)
            addLocation(place, updatePlaceNameAction);
        else {
            isZoneSelected(place.zoneID) ?
                removeZone(place, updatePlaceNameAction) :
                addZone(place, updatePlaceNameAction)
        }
    }
    const onDistrictClick = (zone: Place, district: IPlace, updatePlaceNameAction: () => void): void => {
        state.lastInsertedZone = zone.zoneID

        if (isDistrictSelected(district.zoneID, district.districtID) || areAllDistrictsSelected(zone.zoneID))
            removeDistrict(zone, updatePlaceNameAction, district)
        else
            addDistrict(zone, updatePlaceNameAction, district)
    }
    const addDistrict = (zone: Place, updatePlaceNameAction: () => void, district?: IPlace): void => {

        if (!district) {
            return;
        }


        //Cleaning up the zoneUrl
        state.searchBeanModal.zoneURL = null
        state.searchBeanModal.zoneId = null

        //If almost all the districts are selected, before the insert of the current district...
        if (getDistrictLengthByZoneId(zone.zoneID) - 1 === state.districtSelected[zone.zoneID]?.length) {
            addZone(zone, updatePlaceNameAction);

            if (!state.searchBeanModal.districtIdList)
                return;

            //Removing all the districts previously selected for the specific zone
            state.searchBeanModal.districtIdList = state.searchBeanModal.districtIdList
                .filter(districtID => !state.districtsObj[zone.zoneID].find(district => district.districtID == districtID))

            //Removing the zoneId and its districts from districtSelected
            // $delete(state.districtSelected, zone.zoneID)
            delete state.districtSelected[zone.zoneID]
        }
        //In any other scenario, we will normally add the districts
        else {
            //By default, we remove the zoneId of the district from the searchBeanModal
            const zoneIdxToRemove = state.searchBeanModal.zoneIdList?.findIndex(zoneId => zoneId === zone.zoneID)
            if (zoneIdxToRemove !== -1 && zoneIdxToRemove !== undefined)
                state.searchBeanModal.zoneIdList?.splice(zoneIdxToRemove, 1)

            insertDistrictByZoneId(zone.zoneID, district.districtID)

            if (state.searchBeanModal.districtIdList)
                state.searchBeanModal.districtIdList = [...state.searchBeanModal.districtIdList, district.districtID]
        }

        //Case single district
        if (state.searchBeanModal.districtIdList?.length === 1 && !state.searchBeanModal.zoneIdList?.length) {
            state.searchBeanModal.zoneId = null;
            state.searchBeanModal.zoneURL = null;
            state.searchBeanModal.zoneIdList = []
        }

        if (state.searchBeanModal.zoneIdList && state.searchBeanModal.zoneIdList.length === 1) {
            // @ts-ignore
            state.searchBeanModal.zoneURL = state.zoneList.find(z => z.zoneID === state.searchBeanModal.zoneIdList[0])?.zoneURL
        }
        //Handling the districtUrl and districtId
        else if (isSingleDistrictSelected.value) {
            state.searchBeanModal.districtURL = district.zoneURL
            state.searchBeanModal.districtId = district.districtID
        } else {
            state.searchBeanModal.districtURL = null;
            state.searchBeanModal.districtId = null;
        }
        updatePlaceNameAction();
    }
    const removeDistrict = (zone: IPlace, updatePlaceNameAction: () => void, district?: IPlace): void => {
        if (!(Object.keys(state.districtSelected).length > 0) && !areAllDistrictsSelected(zone.zoneID)) {
            return;
        }
        if (district) {
            //Removing the zone url and id since the districtIds will be inserted in the url
            state.searchBeanModal.zoneId = null
            state.searchBeanModal.zoneURL = null
            //By default, we will also remove the districts URL and id
            state.searchBeanModal.districtURL = null;
            state.searchBeanModal.districtId = null;

            if (!state.districtSelected[zone.zoneID]) {
                //In this scenario the district of the selected zone are all selected just visibly...
                //... which means that the districtObj is empty for that given zoneId
            } else {
                state.districtSelected[zone.zoneID].splice(state.districtSelected[zone.zoneID].indexOf(district.districtID), 1);
            }


            if (state.searchBeanModal.districtIdList)
                //Removing the districtId from the array
                state.searchBeanModal.districtIdList = [...state.searchBeanModal.districtIdList.filter(districtId => district.districtID !== districtId)]

            updatePlaceNameAction()

            /*
            * Case: zone with all districts selected
            *   1) zoneId will be removed
            *   2) remaining district will be added
            * */
            if (areAllDistrictsSelected(zone.zoneID)) {
                removeZone(zone,
                    () => {
                        console.log("")
                    }, true)


                const districtsToAdd = state.districtsObj[district.zoneID].filter(districtToAdd => districtToAdd.districtID !== district.districtID).map(d => d.districtID)

                if (state.searchBeanModal.districtIdList)
                    state.searchBeanModal.districtIdList = [...state.searchBeanModal.districtIdList, ...districtsToAdd]

                state.districtSelected[zone.zoneID] = districtsToAdd

                updatePlaceNameAction()
                return
            }

            /**
             * Case: There is one zone with districts selected
             * */
            if (Object.keys(state.districtSelected).length === 1) {

                /*Case: There is only one district in the selected zone */
                if (Object.values(state.districtSelected)[0].length === 1) {
                    const lastDistrict = state.districtsObj[zone.zoneID].find(district => district.districtID === state.districtSelected[zone.zoneID][0])

                    state.searchBeanModal.districtURL = lastDistrict?.zoneURL;
                    state.searchBeanModal.districtId = lastDistrict?.districtID
                    updatePlaceNameAction()
                }

            }

            /*
            * Case: Zone without districts
            * */
            if (!getDistrictSelectedLengthByZoneId(zone.zoneID)) {
                state.searchBeanModal.districtURL = null;
                state.searchBeanModal.districtId = null;


                if (state.searchBeanModal.zoneId || state.searchBeanModal.zoneIdList?.length === 1)
                    state.searchBeanModal.zoneURL = zone.zoneURL


                // $delete(state.districtSelected, zone.zoneID)
                delete state.districtSelected[zone.zoneID]
                removeZone(zone, updatePlaceNameAction, true)
                //Removing all the
            }

            /*
            * Case: One zone selected with other districts selected too
            * */
            if ((state.searchBeanModal.zoneIdList && state.searchBeanModal.zoneIdList.length === 1) || state.searchBeanModal.zoneId) {
                // @ts-ignore
                state.searchBeanModal.zoneURL = state.zoneList.find(z => z.zoneID === state.searchBeanModal.zoneIdList[0])?.zoneURL
            }
        }
        //Not a district
        else {
            const districtsToRemove = state.districtSelected[zone.zoneID]
            // $delete(state.districtSelected, zone.zoneID)
            delete state.districtSelected[zone.zoneID]

            if (isSingleDistrictSelected.value) {
                const [zoneId, districtIds] = Object.entries(state.districtSelected)[0]
                const lastSingleDistrict = state.districtsObj[+zoneId].find(district => district.districtID === districtIds[0])
                state.searchBeanModal.districtURL = lastSingleDistrict?.zoneURL
                state.searchBeanModal.districtId = lastSingleDistrict?.districtID
            }

            if (districtsToRemove)
                state.searchBeanModal.districtIdList = state.searchBeanModal.districtIdList?.filter(districtId => !districtsToRemove.includes(districtId)) || []
            updatePlaceNameAction();
        }
    }


    return {
        ...toRefs(state),
        //Computed
        isSingleDistrictSelected,
        isAnyDistrictSelected,
        selectedDistrictLength,
        partialSelectedDistrictLength,
        allDistrictSelectedPerZone,
        //Methods
        convertEnergyClassFilter,
        convertFloor,
        resetSearchBeanZonesAndLocalities,
        updateSearchModal,
        updateSearchModalByPlace,
        updateSearchModalAndPlace,
        fixSearchBean,
        initZonesAndLocalities,
        areAllDistrictsSelected,
        isSingleDistrictByZoneId,
        isZoneOrLocalitySelected,
        isZoneWithoutDistrict,
        areZonesAndLocalitiesEmpty,
        areZonesAndLocalitiesFilled,
        getSingleSelectedZone,
        getSingleSelectedDistrict,
        getZoneById,
        getDistrictById,
        getDistrictLengthByZoneId,
        getDistrictSelectedLengthByZoneId,
        resetZoneAndLocalities,
        addZone,
        removeZone,
        isZoneSelected,
        isLocationSelected,
        isDistrictSelected,
        addLocation,
        removeLocation,
        insertDistrictByZoneId,
        insertBulkDistrictByZoneId,
        onPlaceLIClick,
        onDistrictClick,
        addDistrict,
        removeDistrict
    }


}

